import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types/user';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { 
  User as UserIcon, 
  Crown, 
  FileText, 
  Camera,
  Loader2,
  X
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';
import { userService } from '@/services/userService';

interface ProfileSidebarProps {
  user: User;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ user, activeTab, onTabChange }) => {
  const navigate = useNavigate();
  const { logout, updateUser } = useUser();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get avatar URL from user (check both user.avatar and user.profile.avatar for compatibility)
  const avatarUrl = user.avatar || user.profile?.avatar || null;
  
  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: UserIcon },
    { id: 'subscription', label: 'Subscription Plan', icon: Crown },
    { id: 'history', label: 'Learning History', icon: FileText },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out.",
      });
      // Redirect to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Error",
        description: "Failed to logout. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select a valid image file.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image size must be less than 5MB.",
        variant: "destructive",
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      console.error('No file selected');
      toast({
        title: "Error",
        description: "No file selected. Please select an image first.",
        variant: "destructive",
      });
      return;
    }

    console.log('Uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    setIsUploadingImage(true);
    try {
      const response = await userService.uploadProfileImage(file);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Update user context
      if (!response.user) {
        throw new Error('User data not found in response');
      }
      const updatedUser: User = {
        ...user,
        ...response.user,
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
        avatar: response.user.avatar, // Set avatar directly on user object
        profile: {
          ...user.profile,
          ...response.user,
          id: response.user.id || user.profile?.id || user.id,
          avatar: response.user.avatar, // Also set in profile for compatibility
        },
      };
      updateUser(updatedUser);

      toast({
        title: "Success",
        description: "Profile image uploaded successfully!",
      });

      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Failed to upload profile image:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to upload profile image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImagePreview = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getInitials = () => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Profile Picture and Name */}
      <div className="text-center">
        <div className="relative inline-block">
          <div className="relative profile-glow">
            <Avatar className="w-[204px] h-[204px] border-4 border-white overflow-hidden">
              <AvatarImage 
                src={imagePreview || avatarUrl || undefined} 
                alt={`${user.first_name} ${user.last_name}`}
                className="object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-purple-400 to-blue-500 text-white text-xl sm:text-2xl font-bold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            {/* Glowing particles around profile picture - Hidden on small screens for performance */}
            <div className="absolute -inset-2 rounded-full hidden sm:block">
              <div className="absolute top-0 left-0 w-2 h-2 bg-purple-300 rounded-full animate-pulse"></div>
              <div className="absolute top-2 right-0 w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse delay-1000"></div>
              <div className="absolute bottom-0 left-2 w-1 h-1 bg-yellow-300 rounded-full animate-pulse delay-2000"></div>
              <div className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-purple-300 rounded-full animate-pulse delay-3000"></div>
            </div>
            {/* Camera icon */}
            <label
              htmlFor="avatar-upload-sidebar"
              className="absolute bottom-1 right-3 bg-gray-400 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors p-1.5 sm:p-2 cursor-pointer"
            >
              <img src="/camera_1.svg" alt="Camera" className="w-[24px] h-[24px]"/>
              <input
                id="avatar-upload-sidebar"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
        <h2 className="text-white text-lg sm:text-xl font-bold mt-3 sm:mt-4">{user.first_name} {user.last_name || ''}</h2>
        
        {/* Image Upload Controls */}
        {imagePreview && (
          <div className="flex flex-col items-center gap-2 mt-3">
            <div className="flex gap-2">
              <Button
                onClick={handleImageUpload}
                disabled={isUploadingImage}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
              >
                {isUploadingImage ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
              <Button
                onClick={handleRemoveImagePreview}
                disabled={isUploadingImage}
                size="sm"
                variant="outline"
                className="text-white border-gray-600 hover:bg-gray-700 text-xs p-2"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
            <p className="text-xs text-gray-400">Click camera icon to change image</p>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <div className="profile-sidebar-nav">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`profile-sidebar-nav-item ${
                isActive ? 'active' : ''
              }`}
            >
              <Icon className={`w-5 h-5 ${
                isActive ? 'text-[#26265F]' : 'text-white'
              }`} />
              <span className="font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Log Out Button */}
      <Button
        onClick={handleLogout}
        className="profile-logout-button"
      >
        Log Out
      </Button>
    </div>
  );
};
